import { Typography } from '@material-ui/core';
import { createStyles, Theme } from '@material-ui/core/styles';
import { CreateCSSProperties, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { Space } from '@/components';
import TitleButton, { TitleButtonProp } from '@/layout/components/TitleButton';
import { makeSelectIsMobileView } from '@/model/global/selectors';

const useStyles = makeStyles((theme: Theme) => {
  const leftTitleButton: CreateCSSProperties = {
    height: '26px',
  };
  const rightTitleButton: CreateCSSProperties = {
    position: 'absolute',
    right: 0,
    height: '26px',
  };
  return createStyles({
    logoBox: {
      flexShrink: 0,
      overflow: 'visible',
      backgroundColor: theme.colors.lightBackground,
      '& > span': {
        flex: '1',
        height: '68px',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    leftTitleButton,
    rightTitleButton,
    leftTitleButtonMobile: { ...leftTitleButton },
    rightTitleButtonMobile: { ...rightTitleButton },
    headerBox: {
      flexShrink: 0,
      overflow: 'visible',
      padding: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      backgroundColor: theme.colors.primaryBackground,
    },
    headerBoxMobile: {
      flexShrink: 0,
      overflow: 'visible',
      color: theme.colors.primaryBackground,
      padding: theme.spacing(2.5),
      paddingBottom: theme.spacing(1.8),
      paddingTop: theme.spacing(1.8),
    },
    titleBox: {
      flexGrow: 1,
      alignItems: 'flex-start',
      position: 'relative',
    },
  });
});

export type TitleVariant = 'h1' | 'h2' | 'h3' | 'h4';

export interface HeaderBoxProps {
  'data-test'?: string;
  variant?: TitleVariant;
  title?: ReactNode;
  className?: string;
  logo?: { src: string; alt: string };
  buttons?: {
    leftTitle?: TitleButtonProp;
    rightTitle?: TitleButtonProp;
  };
  extra?: ReactNode;
}

const selectIsMobile = makeSelectIsMobileView();

export const HeaderBox: React.FC<HeaderBoxProps> = ({
  variant = 'h1',
  title,
  logo,
  buttons,
  className,
  extra,
  'data-test': dataTest,
}) => {
  const classes = useStyles();
  const isMobile = useSelector(selectIsMobile);
  return (
    <>
      {logo && (
        <Space className={classes.logoBox}>
          <Space>
            <img alt={logo.alt} src={logo.src} data-test={dataTest && `${dataTest}-logo`} />
          </Space>
        </Space>
      )}
      <Space direction="vertical" className={clsx(isMobile ? classes.headerBoxMobile : classes.headerBox, className)}>
        <Space
          className={classes.titleBox}
          size={2}
          style={{ justifyContent: buttons?.leftTitle ? 'inherit' : 'center' }}
        >
          {buttons?.leftTitle && (
            <TitleButton
              data-test={dataTest && `${dataTest}-titleButton`}
              type={buttons.leftTitle}
              className={isMobile ? classes.leftTitleButtonMobile : classes.leftTitleButton}
            />
          )}
          {title ? (
            <Typography
              component="span"
              className={className}
              style={{ flexGrow: 1 }}
              variant={variant}
              data-test={dataTest && `${dataTest}-title`}
            >
              {title}
            </Typography>
          ) : (
            <>&nbsp;</>
          )}
          {buttons?.rightTitle && (
            <TitleButton
              data-test={dataTest && `${dataTest}-rightButton`}
              type={buttons.rightTitle}
              className={isMobile ? classes.rightTitleButtonMobile : classes.rightTitleButton}
            />
          )}
        </Space>
        {extra}
      </Space>
    </>
  );
};

export default HeaderBox;
